import React from 'react';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';

interface RingImpactProps {
    icon: React.ReactElement;
    color: string;
    evolution: number | null;
    label: string;
    currentPercent?: number | null; // 0–100, only for positive evolution
}

const SIZE = 48;
const STROKE_WIDTH = 6;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const RingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Svg = styled.svg`
transform:rotate(-90deg);
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
`;

export const RingImpact: React.FC<RingImpactProps> = ({
    icon,
    color,
    evolution,
    label,
    currentPercent = null,
}) => {
    const isPositive = evolution !== null && evolution > 0;
    const isNegative = evolution !== null && evolution < 0;
    const gainPercent = isPositive ? evolution : 0;
    const basePercent = isPositive && currentPercent ? currentPercent : 0;

    const tooltipText = isNegative
        ? `Réduction de ${Math.abs(evolution)}% représentant ${label}`
        : isPositive
            ? `Gain de ${evolution}% (base: ${currentPercent ?? 0}%) représentant ${label}`
            : `Impact non évalué`;

    return (
        <Tooltip title={tooltipText} arrow>
            <RingContainer>
                <div style={{ position: 'relative', width: SIZE, height: SIZE }}>
                    <Svg width={SIZE} height={SIZE}>
                        {/* Base ring */}
                        <circle
                            cx={SIZE / 2}
                            cy={SIZE / 2}
                            r={RADIUS}
                            stroke="#e0e0e0"
                            strokeWidth={STROKE_WIDTH}
                            fill="none"
                        />

                        {/* For CO2: green ring goes anti-clockwise */}
                        {isNegative && (
                            <circle
                                cx={SIZE / 2}
                                cy={SIZE / 2}
                                r={RADIUS}
                                stroke={color}
                                strokeWidth={STROKE_WIDTH}
                                fill="none"
                                strokeDasharray={`${(Math.abs(evolution!) / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
                                strokeDashoffset={-CIRCUMFERENCE * (1 - Math.abs(evolution!) / 100)}
                                strokeLinecap="round"
                            />
                        )}

                        {/* For Utility: gray base + blue gain */}
                        {isPositive && (
                            <>
                                {/* current score */}
                                <circle
                                    cx={SIZE / 2}
                                    cy={SIZE / 2}
                                    r={RADIUS}
                                    stroke="#525252"
                                    strokeWidth={STROKE_WIDTH}
                                    fill="none"
                                    strokeDasharray={`${(basePercent / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
                                    strokeDashoffset={0}
                                />

                                {/* gain */}
                                <circle
                                    cx={SIZE / 2}
                                    cy={SIZE / 2}
                                    r={RADIUS}
                                    stroke={color}
                                    strokeWidth={STROKE_WIDTH}
                                    fill="none"
                                    strokeDasharray={`${(evolution / 100) * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
                                    strokeDashoffset={((1 - basePercent) / 100 * CIRCUMFERENCE)}
                                />
                            </>
                        )}
                    </Svg>
                    <IconWrapper>{icon}</IconWrapper>
                </div>
                <Label>{label}</Label>
            </RingContainer>
        </Tooltip>
    );
};

export default RingImpact;
